/**
 * @file index.tsx
 *
 * @fileoverview Renders job wizard bundles picker step.
 */

import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import JobWizardBundlesView from '~/jobiqo/components/Job/Wizard/Steps/Bundles/View';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import { JobWizardFlow } from '~/jobiqo/components/Job/Wizard/config';
import Router from 'next/router';
import { useRuntimeStepsInfo } from '~/jobiqo/components/Job/Wizard/hooks/useRuntimeStepsInfo';
import { JobBundle } from '~/jobiqo/models/jobs';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { useConfig } from '~/jobiqo/context/config';
import { useQuery } from '@apollo/client';
import { getCartMetaDataQuery } from '~/jobiqo/components/Checkout/graphql/queries/getCartMetaData';
import { useJobWizardContext } from '~/jobiqo/components/Job/Wizard/context/useJobWizardContext';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface JobWizardBundlesProps {
  /**
   * The get job bundles data.
   */
  data: any;
  /**
   * The query error.
   */
  error: boolean;
  /**
   * The loading flag.
   */
  loading: boolean;
  /**
   * If the wizard is in republishing mode.
   *
   * @default "new"
   */
  flow?: JobWizardFlow;
  /**
   * Job id in the case of a republish flow.
   */
  jobId?: number;
  /**
   * The job type in case of a republish flow.
   */
  jobType: string;
}

export default function JobWizardBundles({
  data,
  error,
  loading,
  flow = JobWizardFlow.New,
  jobId,
  jobType,
}: JobWizardBundlesProps) {
  const { querystring }: PathContextInterface = useContext(PathContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const { JOBS } = useFrontendConfig();
  const SHOW_DESTINATION_STEP = JOBS.JOB_POSTING_WIZARD_SHOW_DESTINATION;
  const { stepsInfo } = useRuntimeStepsInfo(flow);

  const userContext: UserContextInterface = useContext(UserContext);
  const { getConfigByName } = useConfig();
  const SUBSCRIPTIONS_FEATURE_ACTIVE: boolean = !!getConfigByName(
    'jobiqo_subscription',
  )?.value[0];
  const { bundles } = useJobWizardContext();

  const { data: cartMetadataData } = useQuery(
    getCartMetaDataQuery(SUBSCRIPTIONS_FEATURE_ACTIVE),
    {
      fetchPolicy: 'no-cache',
      variables: {
        organizationProfileId: userContext?.currentOrganizationProfile?.id,
      },
    },
  );

  const availableBundles: JobBundle[] = !SHOW_DESTINATION_STEP
    ? data?.jobBundles || []
    : bundles || [];

  let allowedBundles = availableBundles;
  // We do not need the wizard steps here
  // we just directly push to the next step on clicking on job type
  // Check if the current step is the last based on path.

  const currentStep = stepsInfo.findIndex(
    (step) => step.page === pathContext.pathname,
  );

  let errorList: React.ReactNode = null;

  if (error) {
    errorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="job_wizard_bundles_error"
          defaultMessage="There was an error receiving available job bundles."
        />
      </MessageAlert>
    );
  }

  if (jobType && availableBundles) {
    allowedBundles = availableBundles.filter((bundle) =>
      bundle.allowedJobTypes?.find((type) => type?.toLowerCase() === jobType),
    );
  }

  return (
    <JobWizardBundlesView
      Error={errorList}
      cartMetadata={cartMetadataData?.getCartMetaData}
      allowedBundles={allowedBundles}
      availableBundles={availableBundles}
      loading={loading || !bundles}
      flow={flow}
      onSubmit={(bundle: number | null) => {
        if (flow === JobWizardFlow.New) {
          Router.push({
            pathname: stepsInfo[currentStep + 1].page,
            query:
              flow === JobWizardFlow.New
                ? {
                    ...querystring,
                    ...(bundle && { bundle }),
                  }
                : {},
          });
        } else {
          Router.push({
            pathname: stepsInfo[currentStep + 1].page,
            query: {
              jobId: jobId,
              ...(bundle && { bundle }),
            },
          });
        }
      }}
    />
  );
}

export { JobWizardBundles };
